/**
 * html5-qrcode helpers for browsers without BarcodeDetector qr_code support
 * (fallback path when nativeQrScan is unavailable).
 *
 * Mirrored from auth0-sso/src/utils/qrCamera.js.
 */

type ExtendedTrackConstraints = MediaTrackConstraints & {
  focusMode?: string;
  advanced?: Array<Record<string, unknown>>;
};

export const getQrScannerConfig = () => ({
  fps: 10,
  experimentalFeatures: {
    useBarCodeDetectorIfSupported: false,
  },
});

export const getQrCameraIdOrConfig = (): MediaTrackConstraints => ({
  facingMode: 'environment',
});

/** After start(), nudge AF / resolution / zoom when the track allows it. */
export const improveQrCameraFocus = async (scanner: {
  applyVideoConstraints?: (c: MediaTrackConstraints) => Promise<void>;
  getRunningTrackCapabilities?: () => MediaTrackCapabilities;
}): Promise<void> => {
  if (!scanner?.applyVideoConstraints) {
    return;
  }

  await new Promise((resolve) => setTimeout(resolve, 500));

  try {
    const caps =
      scanner.getRunningTrackCapabilities?.() ||
      ({} as MediaTrackCapabilities);

    const constraints: ExtendedTrackConstraints = {
      focusMode: 'continuous',
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    };

    const zoomCap = (caps as MediaTrackCapabilities & { zoom?: any }).zoom;
    if (zoomCap && typeof zoomCap === 'object') {
      const min = zoomCap.min ?? 1;
      const max = zoomCap.max ?? 1;
      if (max > min) {
        constraints.advanced = [{ zoom: Math.min(Math.max(2, min), max) }];
      }
    }

    await scanner.applyVideoConstraints(constraints);
  } catch {
    // ignore unsupported constraints
  }
};

export const cameraErrorMessage = (err: unknown): string => {
  const name = (err as { name?: string })?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'Camera permission was blocked. Allow camera access in the browser address bar and try again.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No camera was found on this device.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Camera is in use by another app. Close it and try again.';
  }
  return 'Could not open the camera. Check permissions and try again.';
};
